// src/hooks/useTextEmbeddings.js
/**
 * Custom hook to fetch embeddings, reduce to 3D, and expose state.
 * Assumes chunks are passed in; does not recompute tokens or chunks.
 */

import { useState, useCallback } from "react";
import { fetchEmbeddingsForChunks, performPCA } from "../utils/embeddingUtils";

/**
 * @typedef {import('./chunk').Chunk} Chunk
 * @typedef {import('./token').Token} Token
 * @typedef {import('./embedding').Embedding} Embedding
 * @typedef {import('./pca').PCA} PCA
 */

/**
 *
 * @param {Object} params
 * @param {number} params.embeddingDimensions
 *
 * @returns {{
 *   enrichedChunks: Chunk[],
 *   isLoading: boolean,
 *   processChunks: (chunks: Chunk[]) => Promise<void>,
 *   pcaResult: PCA | null,
 *   fetchEmbeddingForChunk: (chunk: string) => Promise<number[]>
 * }}
 */
export function useTextEmbeddings({ embeddingDimensions }) {
  const [chunks, setChunks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pcaResult, setPcaResult] = useState(null);
  const [enrichedChunks, setEnrichedChunks] = useState([]);

  // Fetch embedding for a chunk of text
  const fetchEmbeddingForChunk = useCallback(
    async (chunk) => {
      try {
        console.log(
          "[useTextEmbeddings:fetchEmbedding] Fetching embedding for chunk:",
          chunk
        );
        const res = await fetch(
          "/api/getEmbedding.js",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              inputTextChunk: chunk,
              model: "text-embedding-3-small",
              dimensions: embeddingDimensions,
            }),
          }
        );
        if (!res.ok) {
          const msg = `[useTextEmbeddings:fetchEmbedding] Embedding API error: ${res.status} ${res.statusText}`;
          console.error(msg);
          throw new Error(msg);
        }
        // Check if the response is JSON
        const contentType = res.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          const msg = `[useTextEmbeddings:fetchEmbedding] Expected JSON response, got ${contentType}`;
          console.error(msg);
          throw new Error(msg);
        }
        // Parse the JSON response
        // Note: This assumes the response is a single array of numbers
        // If the API returns an object, you may need to adjust this line
        // to extract the embedding array from the object
        // For example, if the API returns { embedding: [1, 2, 3] }, use res.json().then(data => data.embedding)
        // Here we assume the API returns an array of numbers directly
        const data = await res.json();
        if (!Array.isArray(data) || data.length !== embeddingDimensions) {
          const msg = `[useTextEmbeddings:fetchEmbedding] Expected array of length ${embeddingDimensions}, got ${data.length}`;
          console.error(msg);
          throw new Error(msg);
        }
        // Check if the data is an array of numbers
        if (!data.every((num) => typeof num === "number")) {
          const msg = `[useTextEmbeddings:fetchEmbedding] Expected array of numbers, got ${data}`;
          console.error(msg);
          throw new Error(msg);
        }
        // Return the embedding array

        return data;
      } catch (err) {
        console.error(
          "[useTextEmbeddings:fetchEmbedding] Network or parsing error:",
          err
        );
        throw err;
      }
    },
    [embeddingDimensions]
  );

  // Orchestrator: processChunks
  const processChunks = useCallback(
    async (chunks) => {
      setIsLoading(true);
      try {
        if (!chunks || chunks.length === 0) {
          console.warn(
            "[useTextEmbeddings:processChunks] No chunks provided or empty array."
          );
          setChunks([]);
          setPcaResult(null);
          setIsLoading(false);
          return;
        }

        let results;
        try {
          // Use the new embeddingUtils version, passing fetchEmbeddingForChunk
          //
          results = await fetchEmbeddingsForChunks(
            chunks,
            fetchEmbeddingForChunk,
            5
          );
          // log chunk index of count and text
          console.log(
            "[useTextEmbeddings:processChunks] Fetched embeddings for chunks:",
            results.map((_, i) => `Chunk ${i + 1}/${chunks.length}`).join(", ")
          );
        } catch (e) {
          console.error(
            "[useTextEmbeddings:processChunks] Error fetching embeddings:",
            e
          );
          setChunks([]);
          setPcaResult(null);
          setIsLoading(false);
          return;
        }

        // at this point, the chunks have embeddings

        // Assign embeddings to all chunks
        const updatedChunks = chunks.map((chunk, i) => ({
          ...chunk,
          chunkEmbedding: {
            fullEmbedding: results[i],
            dimensions: results[i].length,
          },
        }));

        // If embedding dimensions are less than 3, we can just use the first 3 dimensions
        // Otherwise, we perform PCA to reduce to 3D
        if (embeddingDimensions < 3) {
          console.warn(
            "[useTextEmbeddings:processChunks] Embedding dimensions are less than 3, using first 3 dimensions."
          );
        }
        // Perform PCA to reduce to 3D if embedding dimensions are greater than 3
        let pcaCoords3D,
          matrix,
          meanVector,
          meanVectorDimensions = null;
        if (embeddingDimensions > 3) {
          const retrieved_pcaResult = performPCA(
            updatedChunks.map((chunk) => chunk.chunkEmbedding.fullEmbedding),
            3
          );
          console.log(
            "[useTextEmbeddings:processChunks] PCA performed:",
            retrieved_pcaResult.pcaResult
          );
          pcaCoords3D = retrieved_pcaResult.pcaResult.pcaReduced;
          matrix = retrieved_pcaResult.pcaResult.pcaMatrix;
          meanVector = retrieved_pcaResult.pcaResult.pcaMeanVector;
          meanVectorDimensions =
            retrieved_pcaResult.pcaResult.pcaMeanVectorDimensions;
        } else {
          pcaCoords3D = updatedChunks.map((chunk) => {
            const c = chunk.chunkEmbedding.fullEmbedding.slice(0, 3);
            while (c.length < 3) c.push(0);
            return c;
          });
          matrix = null;
          meanVector = null;
          meanVectorDimensions = 0;
        }

        // Assign PCA to chunks
        const finalChunks = updatedChunks.map((chunk, i) => ({
          ...chunk,
          chunkEmbeddingReduced: {
            fullEmbedding: pcaCoords3D[i],
            dimensions: 3,
          },
        }));

        setChunks(finalChunks);
        setEnrichedChunks(finalChunks);
        setPcaResult({
          pcaMatrix: matrix,
          pcaMeanVector: meanVector,
          pcaMeanVectorDimensions: meanVectorDimensions,
        });
      } catch (err) {
        console.error(
          "[useTextEmbeddings:processChunks] Error processing chunks:",
          err
        );
        setChunks([]);
        setPcaResult(null);
      } finally {
        setIsLoading(false);
      }
    },
    [embeddingDimensions, fetchEmbeddingForChunk]
  );

  return {
    enrichedChunks,
    isLoading,
    processChunks,
    pcaResult,
    fetchEmbeddingForChunk,
  };
}
